/* eslint-disable complexity */
import CloseIcon from '@mui/icons-material/Close';
import DensitySmall from '@mui/icons-material/DensitySmall';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import {useEffect, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {
  BeltMonitoringNode
} from '../../../interfaces/BeltMonitoringNode';
import {MapLatLangCoordinates} from '../../common/HazardMap';
import TruncatedText from '../../common/TruncantedText';
import BeltNodeItemUpdate from './BeltNodeItemUpdate';
import BeltNodeItemView from './BeltNodeItemView';

interface Props {
  pk: number;
  item?: BeltMonitoringNode;
  mode?: 'view' | 'update' | 'ack' | 'update_from_info' | 'active';
  prefetch?: boolean;
  locationCoordinates?: MapLatLangCoordinates;
  onClose?: () => void;
  onSubmitted?: () => void;
  onPurged?: () => void;
  onOpenHistory?: (
    externalId: number,
    type: 'beltHistoryReport' | 'beltLocationHistoryReport'
  ) => void;
}

const BeltNodeItem = ({
  pk,
  item,
  mode: initialMode = 'view',
  prefetch,
  locationCoordinates,
  onClose,
  onSubmitted,
  onPurged,
  onOpenHistory,
}: Props) => {
  const [mode, setMode] = useState(initialMode);

  /*********/
  /* fetch */
  /*********/

  const [fetchedData, setFetchedData] = useState<
    BeltMonitoringNode | undefined
  >(cloneDeep(item));
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchData = async () => {
    setFetchedInProgress(true);
    setFetchedErrors([]);
    try {
      const resp = await API.get<BeltMonitoringNode>(`${apiBaseUrl}/belt/${pk}`);
      setFetchedData(resp.data);

      if (resp.data.acknowledged === 0) {
        setMode('ack');
      }
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_22270753");
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  useEffect(() => {
    if (prefetch) {
      fetchData();
    }
  }, [pk, prefetch]);

  useEffect(() => {
    if (item && !isEqual(item, fetchedData)) {
      setFetchedData(item);
    }
  }, [item]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="relative"
      gap={3}
      p={3}
    >
      <Box display="flex" justifyContent="space-between">
        <Box
          className="dragHandle"
          display="flex"
          alignItems="center"
          width="100%"
          gap={1.5}
          overflow="hidden"
          sx={{cursor: 'grab'}}
        >
          <DensitySmall sx={{fontSize: 24}} />
          {fetchedData ? (
            <TruncatedText fontSize={24}>
              Belt [{fetchedData.commtrac_external_id}]
            </TruncatedText>
          ) : null}
        </Box>
        <Box display="flex" gap={0.5}>
          <IconButton onClick={() => fetchData()}>
            <RefreshIcon />
          </IconButton>
          {onClose ? (
            <IconButton onClick={() => onClose()}>
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </Box>
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error">
          {error}{' '}
        </Alert>
      ))}
      {mode === 'view' ? (
        <BeltNodeItemView
          pk={pk}
          item={fetchedData}
          prefetch={!prefetch}
          onCancel={onClose}
          onEdit={() => setMode('update_from_info')}
          onPurged={onPurged}
          onFetched={setFetchedData}
          onOpenHistory={onOpenHistory}
          onSubmitted={onSubmitted}
        />
      ) : mode === 'update' || mode === 'update_from_info' ? (
        <BeltNodeItemUpdate
          mode={mode}
          pk={pk}
          item={fetchedData}
          prefetch={!prefetch}
          locationCoordinates={locationCoordinates}
          onCancel={() => {
            mode === 'update' ? onClose?.() : setMode('view');
          }}
          onSubmitted={() => {
            onSubmitted?.();
            mode === 'update' ? onClose?.() : setMode('view');
            fetchData();
          }}
        />
      ) : mode === 'ack' ? (
        <>ACK</>
      ) : null}
    </Box>
  );
};

export default BeltNodeItem;
